import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { differenceInCalendarDays } from "date-fns";
import { useGetList, useStore } from "ra-core";
import { Link } from "react-router-dom";

import { RelativeDate } from "../misc/RelativeDate";
import { FOLLOW_UPS_QUEUE_KEY, queueToShow } from "./followUpsRules";

/**
 * The follow-up queue — who is being neglected, in three answers.
 *
 * WHY THIS SCREEN EXISTS
 * ----------------------
 * Zoho held about 45 people untouched for nine months and never said anything
 * was wrong. That is the failure this whole fork is built against, and its
 * mechanism is not a missing report: it is that a record sitting still LOOKED
 * EXACTLY LIKE a record being worked. Nothing distinguished them, so nothing
 * could be escalated.
 *
 * Migration 062 makes the distinction computable — `contact_health` decides
 * when each contact is next due, and three views filter it into the three
 * ways a contact goes quiet. Until this file, all of that was true in the
 * database and invisible in the product, which is very nearly the same
 * failure again: an answer nobody sees is not an answer.
 *
 * THE THREE QUEUES ARE NOT THREE FILTERS ON ONE LIST
 * --------------------------------------------------
 * They ask different questions and two of them are about intent, not time:
 *
 *   overdue        stale, with an open task  — you meant to act and have not
 *   stalled        stale, with no open task  — nobody plans to do anything
 *   no next action not terminal, not paused, no open task — DELIBERATELY
 *                  INDEPENDENT OF STALENESS. A contact created this morning
 *                  with nothing scheduled is already the Zoho failure in its
 *                  first hour; waiting for them to go stale before saying so
 *                  is waiting for the damage.
 *
 * Because the third is not a subset of the first two, collapsing them into
 * one list with a status column would lose the question each one asks.
 *
 * WHY IT READS THE VIEWS RATHER THAN `contacts`
 * ---------------------------------------------
 * The same rule the views themselves obey: `contacts_overdue` and its two
 * siblings select FROM `contact_health` and never recompute staleness, so
 * there is exactly one definition of "late" in the system. This screen keeps
 * that count at one. See `lateness()` below for what that forbids in here.
 */

/**
 * One row of any of the three views.
 *
 * All three expose `contact_health`'s columns unchanged — they are `select *
 * ... where`, so the shape is the view's shape, listed in migration 062.
 *
 * DECLARED HERE, KNOWINGLY. The lifecycle panel (a separate task, in flight)
 * introduces the same row shape in ./types.ts. Once both have landed these
 * should collapse into that one exported type; two hand-written copies of a
 * view's columns is exactly the drift this codebase spends its comments
 * avoiding. Duplicating it for one commit is the smaller cost than two
 * concurrent branches editing the same declaration.
 */
type ContactHealthRow = {
  /** The CONTACT id. The views carry `contacts.id` through untouched, so this
   *  links straight to the contact page without a lookup. */
  id: number;
  first_name: string | null;
  last_name: string | null;
  company_id: number | null;
  lifecycle_stage: string | null;
  track: string | null;
  effective_stale_after: number;
  clock_start: string | null;
  /** NULLABLE, and that is the interesting case — see `lateness()`. */
  due_at: string | null;
  is_stale: boolean;
  is_paused: boolean;
  is_terminal: boolean;
  commitment_at: string | null;
  commitment_note: string | null;
  paused_until: string | null;
  paused_reason: string | null;
  last_contacted_at: string | null;
  source: string | null;
  source_detail: string | null;
  open_tasks: number;
  sales_id: number | null;
};

/**
 * The three queues, in the order they are shown.
 *
 * EVERY QUEUE CARRIES ITS OWN EMPTY SENTENCE, and they are three different
 * sentences on purpose. Two of the three are empty today (63 / 0 / 1), so an
 * empty tab is the normal case here, not an edge case — and a generic "No
 * results" would render the system working correctly as though the screen
 * were broken. Worse, it would read identically whether the queue is empty
 * because everybody is being followed up or because the read failed. The
 * whole product is the difference between those two, so it is spelled out.
 */
const QUEUES = [
  {
    value: "overdue",
    /** Read directly. No client-side filter reproduces this predicate. */
    view: "contacts_overdue",
    label: "Overdue",
    blurb:
      "Stale, and somebody has an open task on them: you meant to act and have not.",
    empty:
      "Nothing is late. Every contact with an open task is still inside its cadence.",
    /** The same news in one clause, for the tab itself when the count is 0. */
    emptyShort: "nothing is late",
  },
  {
    value: "stalled",
    view: "contacts_stalled",
    label: "Stalled",
    blurb:
      "Stale, with no open task: the cadence has run out and nobody has planned a next step.",
    empty:
      "Nobody is stalled. Every stale contact has someone intending to act on them — that is the system working, not an empty screen.",
    emptyShort: "every stale contact has someone intending to act",
  },
  {
    value: "no-next-action",
    view: "contacts_no_next_action",
    label: "No next action",
    blurb:
      "Live, not paused, and no open task — nobody intends to do anything next. Deliberately independent of staleness: this is the Zoho failure made countable, and it is true on day one, not after the clock runs out.",
    empty:
      "Nobody is sitting untended. Every live, unpaused contact has an open task on them.",
    emptyShort: "nobody is sitting untended",
  },
] as const;

type Queue = (typeof QUEUES)[number];

/**
 * The queue names alone, for the stored tab to be checked against.
 *
 * Derived from QUEUES rather than written out, so a queue added, renamed or
 * removed above cannot leave a second, stale list behind — which is the exact
 * failure the check exists to survive.
 */
const QUEUE_VALUES = QUEUES.map((queue) => queue.value);

/**
 * One page is deliberately large but finite.
 *
 * 63 rows today, so everything fits. If this list ever outgrows the page, the
 * rows it drops are by definition the LEAST overdue — but silently showing 100
 * of 400 is how a queue quietly stops being a queue, so `QueuePanel` says so
 * out loud rather than letting the shortfall pass as the whole answer.
 */
const PAGE_SIZE = 100;

/**
 * Most overdue first.
 *
 * A NULL `due_at` sorts last in Postgres ASC, which is the wrong way round for
 * urgency: `contact_health` treats a null due date as due (see its `is_stale`
 * expression, which fails closed on purpose). Those rows are not lost — they
 * are in the list and each says "no due date recorded" in place of a number —
 * but they land at the bottom rather than the top. PostgREST offers no
 * `nullsfirst` through this data provider, and inventing one in JS would mean
 * re-deciding urgency here, which is the one thing this screen must not do.
 */
const SORT = { field: "due_at", order: "ASC" } as const;

const useQueue = (view: string) =>
  useGetList<ContactHealthRow>(view, {
    pagination: { page: 1, perPage: PAGE_SIZE },
    sort: SORT,
    filter: {},
  });

export const FollowUps = () => {
  /*
   * The selected queue, in the app's PERSISTED store rather than in component
   * state — see followUpsRules.ts, and atomic#13 for the rule this follows.
   *
   * `chosen` is what the store holds and may name a queue this build does not
   * have; the render below is what decides which tab that means.
   */
  const [chosen, setChosen] = useStore<string>(
    FOLLOW_UPS_QUEUE_KEY,
    QUEUES[0].value,
  );

  /*
   * All three read on mount, not just the visible tab, because the COUNTS are
   * in the tab labels. A count that only appears once you click the tab
   * answers a question you already had to ask, and the point of putting the
   * number on the tab is that nobody has to think to look.
   *
   * Three explicit hooks rather than a loop: hooks cannot be called from a
   * callback, and a fixed-length list of queues does not need one.
   */
  const results: Record<Queue["value"], ReturnType<typeof useQueue>> = {
    overdue: useQueue("contacts_overdue"),
    stalled: useQueue("contacts_stalled"),
    "no-next-action": useQueue("contacts_no_next_action"),
  };

  return (
    <div>
      <h1 className="text-lg font-semibold">Follow-ups</h1>
      <p className="mt-1 mb-4 max-w-3xl text-sm text-muted-foreground">
        Who is being neglected, computed in the database rather than remembered.
        Each tab is one view over{" "}
        <code className="text-xs">contact_health</code>, so this screen and the
        Monday digest cannot disagree about who is late.
      </p>

      {/*
        CONTROLLED, AND REMEMBERED. `defaultValue` made this screen forget
        which queue you were working the moment you opened a contact and came
        back — on a screen whose whole job is worked one contact at a time.
        `useStore` is `useState` with the app's own persisted store behind it
        (root/CRM.tsx:70); see atomic#13, and followUpsRules.ts for why the
        stored value is checked on the way out rather than trusted.
      */}
      <Tabs value={queueToShow(chosen, QUEUE_VALUES)} onValueChange={setChosen}>
        <TabsList>
          {QUEUES.map((queue) => (
            <QueueTab
              key={queue.value}
              queue={queue}
              result={results[queue.value]}
            />
          ))}
        </TabsList>

        {QUEUES.map((queue) => (
          <TabsContent key={queue.value} value={queue.value} className="pt-3">
            <QueuePanel queue={queue} result={results[queue.value]} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

/**
 * A tab, with its count.
 *
 * NO BARE ZERO. `Stalled 0` is ambiguous in the worst possible direction: it
 * reads as "this screen found nothing", which is what a broken query also
 * looks like. `Stalled · None` reads as an answer, and the tab's title
 * attribute carries the reason so the good news is available without
 * selecting the tab. The full sentence is in the panel.
 *
 * And no number at all while the read is in flight or after it failed — a
 * count is a claim about the database, and a claim must not be made from an
 * answer we do not have. `0` printed during loading is a lie that corrects
 * itself, which is worse than a blank that never lied.
 */
const QueueTab = ({
  queue,
  result,
}: {
  queue: Queue;
  result: ReturnType<typeof useQueue>;
}) => {
  const { total, isPending, error } = result;
  const known = !isPending && !error && total != null;
  const none = known && total === 0;

  return (
    <TabsTrigger
      value={queue.value}
      title={
        none
          ? `${queue.label}: none — ${queue.emptyShort}.`
          : known
            ? `${queue.label}: ${total}. ${queue.blurb}`
            : queue.blurb
      }
    >
      {queue.label}
      {known ? (
        <Badge
          variant={none ? "outline" : "secondary"}
          className={cn("ml-1.5 px-1.5 py-0 text-[0.7rem] font-normal")}
        >
          {none ? "None" : total}
        </Badge>
      ) : null}
    </TabsTrigger>
  );
};

const QueuePanel = ({
  queue,
  result,
}: {
  queue: Queue;
  result: ReturnType<typeof useQueue>;
}) => {
  const { data, total, isPending, error } = result;

  return (
    <div>
      <p className="mb-3 max-w-3xl text-sm text-muted-foreground">
        {queue.blurb}
      </p>

      {/*
        AN ERROR MUST NOT LOOK LIKE GOOD NEWS.
        This is the one screen where a failed read is most dangerous: every
        empty state here is a confident statement that nobody is being
        neglected, and that is precisely the sentence a denied read would
        print. RLS changes, an expired session and a dropped view all surface
        as an error rather than as rows, so the error branch comes first and
        says what it does not know.
      */}
      {error ? (
        <p className="text-sm text-destructive">
          Could not load this queue, so this is not a report that nobody is
          being neglected — it is a report that we do not know.
        </p>
      ) : isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !data?.length ? (
        <p className="text-sm">{queue.empty}</p>
      ) : (
        <>
          <ul className="divide-y rounded-md border">
            {data.map((row) => (
              <ContactRow key={row.id} row={row} />
            ))}
          </ul>
          {(total ?? 0) > data.length ? (
            <p className="mt-2 text-xs text-muted-foreground">
              Showing the {data.length} most overdue of {total}. The rest are
              still in this queue.
            </p>
          ) : null}
        </>
      )}
    </div>
  );
};

/**
 * How late, in days.
 *
 * WHAT THIS FUNCTION IS FORBIDDEN TO DO
 * -------------------------------------
 * It does not decide whether anyone is late. `is_stale` comes from the row,
 * where `contact_health` computed it, and membership of the queue was decided
 * by the view's own WHERE clause. All that happens here is FORMATTING a
 * timestamp the database already produced, in the same spirit as printing a
 * date rather than storing a pre-formatted string.
 *
 * The distinction matters because the alternative has a specific failure
 * mode. If JS re-derived lateness from `due_at` and `Date.now()`, then a
 * browser clock a few hours out, a timezone assumption, or a rounding choice
 * would put this screen and the Monday digest — which reads the same views
 * server-side — into disagreement about the same person on the same day. At
 * that point neither can be trusted, and an accountability tool nobody trusts
 * is worse than none: it gets ignored and the nine-month silence comes back.
 * It is the reason the three views select FROM `contact_health` instead
 * of recomputing staleness, and it applies one layer up just as much.
 *
 * So:
 *   - `is_stale` (SQL) says late or not late.
 *   - the day count (JS) only says how big the gap is.
 *   - where the two would conflict — SQL says stale, the calendar difference
 *     rounds to zero — SQL wins and no number is shown.
 */
const lateness = (row: ContactHealthRow) => {
  /*
   * No due date at all. `contact_health` fails closed here on purpose:
   * `due_at is null or now() > due_at`, so a contact with no clock counts as
   * due rather than disappearing. The naive form would have hidden exactly
   * the most neglected records, so this case is labeled rather than blank.
   */
  if (!row.due_at) return { text: "No due date recorded — counts as due" };

  const days = differenceInCalendarDays(new Date(), new Date(row.due_at));

  if (row.is_stale) {
    // Stale but the calendar difference is 0 or negative: the view's `now()`
    // and this browser's clock disagree at the margin. The view is the one the
    // digest also reads, so it wins and we simply decline to print a number.
    if (days < 1) return { text: "Overdue", late: true };
    return {
      text: `${days} ${days === 1 ? "day" : "days"} overdue`,
      late: true,
    };
  }

  // Not stale. Only `no next action` puts these on screen — it is deliberately
  // independent of staleness — and printing "-6 days overdue" for someone who
  // is simply not due yet would make the queue look wrong rather than the
  // contact look untended.
  if (days === 0) return { text: "Due today" };
  return { text: `Due in ${-days} ${days === -1 ? "day" : "days"}` };
};

const ContactRow = ({ row }: { row: ContactHealthRow }) => {
  const name = [row.first_name, row.last_name].filter(Boolean).join(" ");
  const { text, late } = lateness(row);

  return (
    <li className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-3 py-2 text-sm">
      <Link
        to={`/contacts/${row.id}/show`}
        className="font-medium underline-offset-2 hover:underline"
      >
        {/* A contact with neither name still has to be reachable — an
            unnamed row silently dropped from the queue is the failure. */}
        {name || `Contact ${row.id}`}
      </Link>

      <span
        className={cn(
          "text-xs",
          late ? "text-destructive" : "text-muted-foreground",
        )}
      >
        {text}
      </span>

      {/* The date itself, next to the derived phrase, so the number above can
          always be checked against the value the view produced. */}
      {row.due_at ? (
        <span className="text-xs text-muted-foreground">
          due <RelativeDate date={row.due_at} />
        </span>
      ) : null}

      <span className="text-xs text-muted-foreground">
        {row.last_contacted_at ? (
          <>
            last contacted <RelativeDate date={row.last_contacted_at} />
          </>
        ) : (
          // Not "no data". A contact nobody has ever reached is the case this
          // system exists to surface, and it deserves words rather than a gap.
          "never contacted"
        )}
      </span>

      <span className="ml-auto flex items-center gap-2">
        <Badge variant="outline" className="text-[0.7rem] font-normal">
          {/* No lifecycle row means no stage, and that is its own finding:
              the contact fell through triage. `contact_health` LEFT-joins so
              they still appear here, on the 7-day floor. */}
          {row.lifecycle_stage
            ? [row.lifecycle_stage, row.track].filter(Boolean).join(" · ")
            : "untriaged"}
        </Badge>
        <span className="text-xs text-muted-foreground">
          {row.open_tasks === 1
            ? "1 open task"
            : `${row.open_tasks} open tasks`}
        </span>
      </span>
    </li>
  );
};
